import { useCallback, useEffect, useState } from 'react';

import { fetchChatSchedule } from '../../../common/fetch.js';
import { isInBrowser } from '../../../common/utils/ssrUtils.js';
import { PROACTIVE_CHAT_DISMISSED_KEY, PROACTIVE_TRIGGER_DELAY_MS } from '../constants.js';
import { hasChatBeenOpened, isChatScheduleOpen, toggleGenesysWidgetVisibility } from '../utils.js';
import { getElisaChatClient } from './useGenesysChat.js';
import type { ChatScheduleResponse } from '../../../generated/api/chatScheduleResponse.js';

interface UseProactiveChatTriggerOptions {
  isSalesPage: boolean;
  delayMs?: number;
  /** pathname dependency — resets timer on route change */
  pathname: string;
}

interface UseProactiveChatTriggerReturn {
  isVisible: boolean;
  dismiss: () => void;
  openChat: () => void;
}

const isDismissed = (): boolean => {
  if (!isInBrowser()) {
    return false;
  }
  return sessionStorage.getItem(PROACTIVE_CHAT_DISMISSED_KEY) !== null;
};

const markDismissed = (): void => {
  if (isInBrowser()) {
    sessionStorage.setItem(PROACTIVE_CHAT_DISMISSED_KEY, '1');
  }
};

const pushElisaChatEvent = (eventName: string): void => {
  if (!isInBrowser()) {
    return;
  }

  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  (window.dataLayer as Array<Record<string, unknown>>).push({
    event: 'genesys_chat',
    event_name: eventName, // eslint-disable-line @typescript-eslint/naming-convention
  });
};

export const useProactiveChatTrigger = ({
  isSalesPage,
  delayMs = PROACTIVE_TRIGGER_DELAY_MS,
  pathname,
}: UseProactiveChatTriggerOptions): UseProactiveChatTriggerReturn => {
  const [isVisible, setIsVisible] = useState(false);
  const [schedule, setSchedule] = useState<ChatScheduleResponse | null>(null);

  useEffect(() => {
    if (!isSalesPage || isDismissed()) {
      return;
    }

    const controller = new AbortController();
    fetchChatSchedule({ signal: controller.signal })
      .then(setSchedule)
      .catch(() => setSchedule(null));

    return () => controller.abort();
  }, [isSalesPage]);

  useEffect(() => {
    if (!isSalesPage || isDismissed() || !schedule) {
      return;
    }

    const timerId = setTimeout(() => {
      if (!isDismissed() && !hasChatBeenOpened() && isChatScheduleOpen(schedule)) {
        // Hide synchronously alongside showing the card so the icon can't
        // linger for a frame (or longer, if the main thread is busy).
        toggleGenesysWidgetVisibility(false);
        setIsVisible(true);
        pushElisaChatEvent('chat_banner_display');
      }
    }, delayMs);

    return () => {
      clearTimeout(timerId);
      toggleGenesysWidgetVisibility(true);
      setIsVisible(false);
    };
    // pathname in deps resets timer on route change
  }, [isSalesPage, delayMs, pathname, schedule]);

  const closeBanner = useCallback(() => {
    toggleGenesysWidgetVisibility(true);
    markDismissed();
    setIsVisible(false);
  }, []);

  const dismiss = useCallback(() => {
    closeBanner();
    pushElisaChatEvent('chat_banner_dismiss');
  }, [closeBanner]);

  const openChat = useCallback(() => {
    // Restore the widget before opening — the chat window lives inside the same host.
    getElisaChatClient()?.open();
    closeBanner();
    pushElisaChatEvent('chat_banner_start_chat');
  }, [closeBanner]);

  return { isVisible, dismiss, openChat };
};
